/**
 * 🔦 The torch — where one may be put, which way it points, and when it falls.
 *
 * **T128 · D90 · [`Minecraft/02_Building_Mechanics.md`](../../../Development/Minecraft/02_Building_Mechanics.md) §1.2 and §2.2**
 *
 * ## What this file is
 *
 * **All of the torch's rules and none of its drawing.** Pure arithmetic over
 * block ids, so the same answers are given on the server, which owns the world,
 * and on the client, which draws it — the arrangement `shelter.ts` already
 * uses, and for the same reason: **one function is what stops the picture on
 * screen disagreeing with the truth in the database.**
 *
 * ## 🎯 The design, and it is all haugur's
 *
 * | | |
 * |---|---|
 * | **D90, 2026-08-25** | *"if you place a torch it automatically lights up and burns forever"* |
 * | **2026-08-31** | *"a torch can be placed on most blocks, sits in the middle, either ontop or side of block"* |
 * | **2026-08-31** | *"you can break it and get it back to inventory"* |
 * | **2026-08-31**, shown an unlit torch as a mockup | **keep D90 as it is** — there is no unlit torch |
 *
 * ## 🔴 Orientation is never asked for
 *
 * `Minecraft/02` §1.2: **nothing in the placement interface asks which way round
 * a block should go.** The game reads it off *which face you clicked*, and the
 * document's verdict is that a building game whose blocks needed an orientation
 * menu *"would lose the thing that makes Minecraft building feel like
 * drawing."* ✅ **We already have everything needed for that**, because
 * `raycastVoxels` returns both the block it hit and the empty cell in front of
 * it — and the difference between those two IS the face.
 *
 * ## ⚠️ Support is about the SHAPE of the face, not about "solid"
 *
 * §2.2 is the part that was expected to be simpler than it is. A face is not
 * merely supporting or not; the wiki distinguishes three surface shapes, and
 * the torch uses two of them:
 *
 * | | Needs | On which side |
 * |---|---|---|
 * | **Standing torch** | a **small** square | the top of the block below |
 * | **Wall torch** | a **full** square | the side of the block behind |
 *
 * 🔴 **That difference is real and it is not pedantry.** A hearth is a quarter
 * of a metre tall: **you can stand a torch on it**, because its top is a small
 * square — but **you cannot hang one off its side**, because there is barely a
 * side there to nail to. Collapsing both to `isSolid` would allow the second.
 */

import { blockCutout, blockHeight, blockIdForKey, isFluid, isSolid, BLOCK_AIR } from './blocks.ts';
import type { WorldPosition } from './coords.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';

export const TORCH_KEY = 'kyndill';

/**
 * Which way a torch leans, worst-named-first-value last.
 *
 * 🔴 **`up` is first and that is load-bearing**, not alphabetical: the first
 * value of a property is its default, and the default combination **is the base
 * block's own id** rather than a new number. Standing a torch on the ground is
 * by far the commonest case, so it is the one that costs nothing.
 *
 * ⚠️ **A compass value names the direction the torch LEANS**, not the wall it
 * is fixed to. `north` means it leans north, so the wall is to its **south**.
 * Getting this backwards would put every wall torch inside the wall, and it
 * would look like a drawing bug rather than a rule one.
 */
export const TORCH_FACINGS = ['up', 'north', 'east', 'south', 'west'] as const;

export type TorchFacing = (typeof TORCH_FACINGS)[number];

/**
 * Which way each compass facing leans, in world axes.
 *
 * ⚠️ **North is −z in this world** — `world.ts`, because z is the negated ISN93
 * northing, and `shelter.ts` already depends on the same fact for the lee of a
 * wall. **Getting the sign wrong here is invisible**: the torch simply attaches
 * to the wrong side and nothing fails.
 */
const LEAN: ReadonlyMap<TorchFacing, WorldPosition> = new Map([
  ['up', { x: 0, y: 1, z: 0 }],
  ['north', { x: 0, y: 0, z: -1 }],
  ['east', { x: 1, y: 0, z: 0 }],
  ['south', { x: 0, y: 0, z: 1 }],
  ['west', { x: -1, y: 0, z: 0 }],
]);

/** Is this number a torch, whichever way it is facing? */
export function isTorch(id: number): boolean {
  const base = blockIdForKey(TORCH_KEY);
  return base !== undefined && baseBlockOf(id) === base;
}

/** Which way this torch leans, or `undefined` if it is not a torch. */
export function torchFacing(id: number): TorchFacing | undefined {
  if (!isTorch(id)) return undefined;
  const facing = stateOf(id)['facing'];
  return typeof facing === 'string' && (TORCH_FACINGS as readonly string[]).includes(facing)
    ? (facing as TorchFacing)
    : 'up';
}

/** The number for a torch leaning this way, or `undefined` if it was never locked. */
export function torchIdFor(facing: TorchFacing): number | undefined {
  const base = blockIdForKey(TORCH_KEY);
  if (base === undefined) return undefined;
  return withState(base, { facing });
}

/**
 * 🔴 **Which way a torch put HERE would lean — read off the face you clicked.**
 *
 * @param anchor the solid block the crosshair hit.
 * @param at the empty cell in front of it, where the torch would go. These are
 *   exactly `VoxelHit.block` and `VoxelHit.place`, and **the difference between
 *   them is the face** — which is why no extra information has to be sent.
 * @returns the facing, or `null` if a torch cannot go there at all.
 *
 * ⬜ **Clicking the UNDERSIDE of a block answers `null`.** A torch hanging from
 * a ceiling is a lantern and we have no lantern; `block-states.json` records
 * that there is deliberately no `down` facing. ⚠️ Minecraft refuses this too.
 */
export function torchFacingFrom(anchor: WorldPosition, at: WorldPosition): TorchFacing | null {
  const dx = at.x - anchor.x;
  const dy = at.y - anchor.y;
  const dz = at.z - anchor.z;

  /* Not adjacent, or diagonal. Neither can come out of a raycast hit, and
     answering null rather than guessing keeps a future caller honest. */
  if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) !== 1) return null;

  if (dy === 1) return 'up';
  if (dy === -1) return null;
  if (dx === 1) return 'east';
  if (dx === -1) return 'west';
  if (dz === 1) return 'south';
  return 'north';
}

/**
 * The cell that holds a torch up — the block it is fixed to.
 *
 * ⚠️ **The opposite of the lean.** A torch leaning east is nailed to the wall on
 * its west side; one standing up is held by the ground beneath it.
 */
export function torchAnchor(at: WorldPosition, facing: TorchFacing): WorldPosition {
  /* A Map rather than `LEAN[facing]`: indexing a plain object with a variable is
     the object-injection pattern the linter flags, and every other file here
     answers it with a Map instead of suppressing it (`blocks.ts`, `states.ts`).
     ⚠️ The fallback is `up` rather than a throw — a facing that is not in the
     map cannot come from `torchFacingFrom`, and standing the torch on the ground
     degrades better than taking the tick down. */
  const lean = LEAN.get(facing) ?? { x: 0, y: 1, z: 0 };
  return { x: at.x - lean.x, y: at.y - lean.y, z: at.z - lean.z };
}

/**
 * 🔴 **A face big enough to nail a torch to** — §2.2's *full square*.
 *
 * A whole cube, solid all the way through and with no holes in it. ⚠️ **A birch
 * crown fails on the holes and a hearth fails on the height**, and both of those
 * are the rule working rather than an omission.
 */
export function holdsAWallTorch(id: number): boolean {
  if (id === BLOCK_AIR || isFluid(id)) return false;
  if (blockCutout(id) > 0) return false;
  return isSolid(id) && blockHeight(id) >= 1;
}

/**
 * 🔴 **A face big enough to stand a torch on** — §2.2's *small square*.
 *
 * Anything solid you could set something down on, **including a block that is
 * only a quarter of a metre tall.** ⚠️ **This is deliberately weaker than
 * {@link holdsAWallTorch}**: you can stand a torch on a hearth and you cannot
 * hang one off its side, which is exactly the distinction §2.2 draws.
 */
export function holdsAStandingTorch(id: number): boolean {
  if (id === BLOCK_AIR || isFluid(id)) return false;
  if (blockCutout(id) > 0) return false;
  return isSolid(id);
}

/**
 * 🔴 **Can a torch leaning this way survive against this block?**
 *
 * This is Minecraft's `can_survive`, and §2.1 says it is asked at **two**
 * moments: when the torch is placed, and again whenever a neighbour changes.
 * **The second is what makes a torch fall when you mine the wall out from under
 * it** — and the document is blunt about the cost of not having it: *"without
 * it, torches float, doors survive their doorframes and every build accumulates
 * orphaned decoration."*
 */
export function torchSurvivesOn(anchorId: number, facing: TorchFacing): boolean {
  return facing === 'up' ? holdsAStandingTorch(anchorId) : holdsAWallTorch(anchorId);
}

/**
 * Where a torch anchored to `anchor` and leaning this way would sit.
 *
 * ⚠️ **The inverse of {@link torchAnchor}**, and it exists so the two live in
 * one file rather than one of them being re-derived at a call site. A server
 * that worked out "which cells could hold a torch fixed to this one" by hand
 * would be a second definition of where a torch sits.
 */
export function torchCellFor(anchor: WorldPosition, facing: TorchFacing): WorldPosition {
  const lean = LEAN.get(facing) ?? { x: 0, y: 1, z: 0 };
  return { x: anchor.x + lean.x, y: anchor.y + lean.y, z: anchor.z + lean.z };
}

/**
 * 🔴 **Which torches this edit knocks down.**
 *
 * `Minecraft/02` §2.1: a block that needs an anchor has its survival checked at
 * **two** moments — when it is placed, and again **whenever a neighbour
 * changes**. The second is what makes a torch fall when you mine the wall out
 * from under it, and the document is blunt about the cost of not having it:
 * *"without it, torches float, doors survive their doorframes and every build
 * accumulates orphaned decoration."*
 *
 * @param at the cell being edited.
 * @param becomes what it is about to hold — **air for a break, a block id for a
 *   placement.** ⚠️ Both can orphan a torch: mining a wall away removes the
 *   support, and **replacing it with something that will not hold one** — scrub,
 *   a hearth, another torch — takes it away just as surely.
 * @param blockAt reads the world as it is *now*, before the edit.
 * @returns every cell holding a torch that can no longer stay up. **Empty is by
 *   far the commonest answer** and costs five reads.
 *
 * ⚠️ **Only the FIVE cells a torch could be anchored to this one from.** A torch
 * two blocks away is somebody else's problem, and `can_survive` is deliberately
 * local — §2 is emphatic that attachment asks *"is my one neighbour a valid
 * anchor?"* and never *"what is holding this up?"*
 *
 * 🔴 **The cell being edited is NOT included.** A torch standing in the cell you
 * are filling in is destroyed by the edit rather than orphaned by it, and the
 * caller has to notice that separately — they are two different losses and only
 * one of them is a cascade.
 */
export function torchesOrphanedBy(
  at: WorldPosition,
  becomes: number,
  blockAt: (x: number, y: number, z: number) => number,
): WorldPosition[] {
  const out: WorldPosition[] = [];
  for (const facing of TORCH_FACINGS) {
    const cell = torchCellFor(at, facing);
    const id = blockAt(cell.x, cell.y, cell.z);
    /* ⚠️ It has to be a torch AND leaning the right way. A torch beside this
       cell that hangs off a different wall is none of our business. */
    if (torchFacing(id) !== facing) continue;
    if (torchSurvivesOn(becomes, facing)) continue;
    out.push(cell);
  }
  return out;
}

/**
 * ✅ **Everything a placement needs, in one answer.**
 *
 * @returns the block number to write, or `null` if a torch cannot go there —
 *   because the face is the underside, or because the block behind will not
 *   hold one.
 *
 * ⚠️ **The caller still has to check the cell is empty and within reach.** Those
 * are true of placing anything and they are not this file's business.
 */
export function placeTorch(
  anchor: WorldPosition,
  at: WorldPosition,
  anchorId: number,
): { readonly id: number; readonly facing: TorchFacing } | null {
  const facing = torchFacingFrom(anchor, at);
  if (facing === null) return null;
  if (!torchSurvivesOn(anchorId, facing)) return null;
  const id = torchIdFor(facing);
  /* 🔴 Loud rather than silent: `withState` answers undefined rather than
     falling back to the base block, so a facing that was never locked would
     otherwise place an UP torch on a wall and nothing would say so. */
  if (id === undefined) return null;
  return { id, facing };
}
